import React, { useState, useEffect, useRef } from 'react';
import { 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface SigningRecipient {
  recipientId: string;
  recipientEmail: string;
  recipientName: string;
  status: string;
  signingUrl?: string;
  error?: string;
  message?: string;
}

interface EmbeddedSignatureProps {
  facilityId: string;
  documentType: 'deal_documents' | 'term_sheet' | 'exercise_notice' | 'settlement';
  onSigningComplete?: () => void;
  onClose?: () => void;
}

const EmbeddedSignature: React.FC<EmbeddedSignatureProps> = ({
  facilityId,
  documentType,
  onSigningComplete,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signatureStatus, setSignatureStatus] = useState<string>('not_started');
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [showFrame, setShowFrame] = useState(false);
  const [recipients, setRecipients] = useState<SigningRecipient[]>([]);
  const [currentRecipient, setCurrentRecipient] = useState<SigningRecipient | null>(null);
  const [envelopeId, setEnvelopeId] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    checkSignatureStatus();
  }, [facilityId, documentType]);

  // // Listen for postMessage from SignatureAPI iframe
  // useEffect(() => {
  //   const handleMessage = (event: MessageEvent) => {
  //     // Verify origin for security
  //     if (event.origin !== process.env.REACT_APP_SIGNATUREAPI_URL) return;
      
  //     if (event.data.event === 'signing_complete') {
  //       if (onSigningComplete) {
  //         onSigningComplete();
  //       }
  //     } else if (event.data.event === 'signing_cancelled') {
  //       setShowFrame(false);
  //       setError('Signing was cancelled');
  //     }
  //   };

  //   window.addEventListener('message', handleMessage);
  //   return () => window.removeEventListener('message', handleMessage);
  // }, []);

  const handleComplete = () => {
    console.log('Signature process completed');
    if (onSigningComplete) {
      onSigningComplete();
    }
    if (onClose) {
      onClose();
    }
  };

useEffect(() => {
  const handleMessage = (event: MessageEvent) => {
    console.log("📩 postMessage received:", event.data, "from:", event.origin);

    // only listen to SignNow's domain
    if (event.origin !== "https://app.signnow.com") return;

    // SignNow uses `event: "change-route"` after signing
    if (
      event.data?.event === "change-route" &&
      typeof event.data?.path === "string" &&
      event.data.path.includes("/thank-you")
    ) {
      console.log("✅ Signing completed!");
      handleComplete();
    }
  };

  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
}, [handleComplete]);


  const checkSignatureStatus = async () => {
    try {
      setLoading(true);
      
      const urlDocType = documentType.replace(/_/g, '-'); // Replace ALL underscores
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/signature/${urlDocType}/status/${facilityId}`;
      const token = localStorage.getItem('token');
      
      console.log('=== CHECKING SIGNATURE STATUS ===');
      console.log('URL:', url);
      console.log('Token exists:', !!token);
      console.log('Token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Status check response:', response.data);
      
      if (response.data.success) {
        setSignatureStatus(response.data.status.status || response.data.status);
      }
    } catch (error: any) {
      console.error('=== ERROR CHECKING SIGNATURE STATUS ===');
      console.error('Error:', error);
      console.error('Response:', error.response);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      
      // Provide specific error messages based on status code
      if (error.response?.status === 401) {
        console.error('Authentication error - user may have been logged out');
        setError('Session expired. Please refresh the page and log in again.');
      } else if (error.response?.status === 404) {
        console.error('Document not found or not yet created');
        setError('Document not found. Please ensure the document has been created.');
      } else if (error.response?.status === 500) {
        console.error('Server error while checking signature status');
        setError('Server error. Please try again in a moment.');
      } else {
        // Don't set an error for status checking - just log it
        console.log('Status check failed, documents may not be created yet');
        setSignatureStatus('not_started');
      }
    } finally {
      setLoading(false);
    }
  };

  const getMultiPartySigningUrls = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/signature/${documentType.replace(/_/g, '-')}/multi-party-signing-urls/${facilityId}`;
      const token = localStorage.getItem('token');
      
      console.log('=== GETTING MULTI-PARTY SIGNING URLS ===');
      console.log('URL:', url);
      console.log('Token exists:', !!token);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Multi-party signing response:', response.data);
      
      if (response.data.success && response.data.data) {
        const { envelopeId, signingUrls, nextSigner } = response.data.data;
        
        setEnvelopeId(envelopeId);
        setRecipients(signingUrls);
        
        if (nextSigner && nextSigner.signingUrl) {
          setCurrentRecipient(nextSigner);
          setSigningUrl(nextSigner.signingUrl);
          setShowFrame(true);
        } else {
          // Check if all are completed
          const allCompleted = signingUrls.every((r: SigningRecipient) => r.status === 'completed');
          if (allCompleted) {
            setSignatureStatus('completed');
          } else {
            setError('No available signing URL found. Please check the document status.');
          }
        }
      } else {
        throw new Error(response.data.message || 'No signing URLs received');
      }
    } catch (error: any) {
      console.error('=== ERROR GETTING MULTI-PARTY SIGNING URLS ===');
      console.error('Error:', error);
      console.error('Response:', error.response);
      console.error('Response data:', error.response?.data);
      
      if (error.response?.status === 400) {
        setError('Failed to get signing URLs. Please try again.');
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(error.response?.data?.message || error.message || 'Failed to get signing URLs');
      }
    } finally {
      setLoading(false);
    }
  };

  const getSigningUrl = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      // First, try to get signing URL directly
      const urlDocType = documentType.replace(/_/g, '-'); // Replace ALL underscores
      const signingUrlEndpoint = `${baseUrl}/api/signature/${urlDocType}/signing-url/${facilityId}`;
      
      console.log('=== GETTING SIGNING URL ===');
      console.log('URL:', signingUrlEndpoint);
      console.log('Token exists:', !!token);
      
      try {
        const response = await axios.get(signingUrlEndpoint, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('Signing URL response:', response.data);
        
        if (response.data.success && response.data.signingUrl) {
          setSigningUrl(response.data.signingUrl);
          setShowFrame(true);
          
          // If it's a test mode envelope, show a notice
          if (response.data.mode === 'test') {
            console.log('Envelope is in test mode');
          }
          return;
        }
      } catch (signingUrlError: any) {
        console.log('Direct signing URL failed, trying to initiate signing first...');
        
        // If documents don't exist yet, initiate signing first
        if (signingUrlError.response?.status === 400 && 
            signingUrlError.response?.data?.message?.includes('not yet created')) {
          
          console.log('=== INITIATING SIGNING FIRST ===');
          
          // Call initiate-signing to create documents
          const initiateResponse = await axios.post(`${baseUrl}/api/signature/initiate-signing`, {
            facilityId: facilityId,
            documentType: documentType
          }, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          console.log('Initiate signing response:', initiateResponse.data);
          
          if (initiateResponse.data.success && initiateResponse.data.signingUrl) {
            setSigningUrl(initiateResponse.data.signingUrl);
            setShowFrame(true);
            return;
          }
          
          // If initiate-signing worked, try getting signing URL again
          const retryResponse = await axios.get(signingUrlEndpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (retryResponse.data.success && retryResponse.data.signingUrl) {
            setSigningUrl(retryResponse.data.signingUrl);
            setShowFrame(true);
            return;
          }
        }
        
        // Re-throw the original error if it's not the "documents not created" case
        throw signingUrlError;
      }
      
      throw new Error('Failed to get signing URL');
      
    } catch (error: any) {
      console.error('=== ERROR GETTING SIGNING URL ===');
      console.error('Error:', error);
      console.error('Response:', error.response);
      console.error('Response data:', error.response?.data);
      
      if (error.response?.status === 400) {
        setError('Failed to get signing URL. Please try again.');
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(error.response?.data?.message || error.message || 'Failed to get signing URL');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSigningComplete = async () => {
    setShowFrame(false);
    setSignatureStatus('completed');
    
    // Refresh status
    await checkSignatureStatus();
    
    if (onSigningComplete) {
      onSigningComplete();
    }
  };

  const handleResend = async () => {
    try {
      setLoading(true);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/signature/${documentType.replace('_', '-')}/resend/${facilityId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        alert('Signature request has been resent to your email.');
      }
    } catch (error) {
      console.error('Error resending signature:', error);
      setError('Failed to resend signature request');
    } finally {
      setLoading(false);
    }
  };



  const resendDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/signature/${documentType.replace('_', '-')}/resend/${facilityId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        await checkSignatureStatus();
      }
    } catch (error) {
      console.error('Error resending documents:', error);
      setError('Failed to resend documents');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (signatureStatus) {
      case 'completed':
        return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
      case 'sent':
      case 'viewed':
        return <ClockIcon className="w-6 h-6 text-yellow-500" />;
      case 'declined':
      case 'voided':
        return <ExclamationCircleIcon className="w-6 h-6 text-red-500" />;
      default:
        return <DocumentTextIcon className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    switch (signatureStatus) {
      case 'completed':
        return 'Signed';
      case 'sent':
        return 'Sent - Awaiting Signature';
      case 'viewed':
        return 'Viewed - Awaiting Signature';
      case 'declined':
        return 'Declined';
      case 'voided':
        return 'Voided';
      default:
        return 'Not Started';
    }
  };

  const getDocumentTitle = () => {
    switch (documentType) {
      case 'deal_documents':
        return 'Facility Agreement Documents';
      case 'term_sheet':
        return 'Term Sheet';
      case 'exercise_notice':
        return 'Exercise Notice';
      case 'settlement':
        return 'Settlement Statement';
      default:
        return 'Document';
    }
  };

  if (loading && !showFrame) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (showFrame && signingUrl) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{getDocumentTitle()}</h3>
            <button
              onClick={() => {
                setShowFrame(false);
                if (onClose) onClose();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Signing Frame */}
          {showFrame && signingUrl && (
            <div className="bg-white rounded-lg shadow-sm p-1">
              <iframe
                ref={iframeRef}
                src={signingUrl}
                className="w-full h-[600px] border-0 rounded-lg"
                title="Document Signature"
                onLoad={() => {
                  console.log('Iframe loaded');
                  // Listen for completion messages if SignatureAPI sends them
                  window.addEventListener('message', (event) => {
                    console.log('Received Message in Embedded Signature:', event.data);
                    if (event.data.type === 'signature_completed' || event.data.type === 'ceremony_completed') {
                      handleComplete();
                    }
                  });
                }}
              />
              <div className="mt-4 text-sm text-secondary-600 text-center">
                <p>Complete the signature process in the frame above.</p>
                <p className="mt-2">Once you've signed all documents, click the button below:</p>
                <button
                  onClick={handleComplete}
                  className="mt-4 btn-primary btn-sm"
                >
                  I've Completed Signing
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          {getStatusIcon()}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{getDocumentTitle()}</h3>
            <p className="text-sm text-gray-500 mt-1">{getStatusText()}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {signatureStatus === 'sent' || signatureStatus === 'viewed' ? (
            <>
              <button
                onClick={getSigningUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Sign Now
              </button>
              <button
                onClick={resendDocuments}
                className="p-2 text-gray-600 hover:text-gray-800"
                title="Resend documents"
              >
                <ArrowPathIcon className="w-5 h-5" />
              </button>
            </>
          ) : signatureStatus === 'not_started' ? (
            <button
              onClick={getSigningUrl}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Signing
            </button>
          ) : signatureStatus === 'completed' ? (
            <span className="text-green-600 font-medium">✓ Completed</span>
          ) : null}
        </div>
      </div>
      
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      
      {signatureStatus === 'completed' && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700">
            Documents have been signed successfully. You can download the signed copies from your dashboard.
             onSigningComplete();
        
          </p>
        </div>
      )}
    </div>
  );
};

export default EmbeddedSignature; 